import type { Message } from "./dead-letter";

export interface DeadLetterPage {
	messages: Message[];
	/** The next page number, or null when this was the last. */
	nextPage: number | null;
}

/**
 * HA-879 — what Wolverine's `POST /dead-letters` really answers: an ARRAY, one result per message store,
 * each `{ totalCount, pageNumber, databaseUri, envelopes }`. A dead letter in `envelopes` carries its body
 * as `message` (and the raw `envelope`), and paging is by page number: there is no `nextId`. Pinned by the
 * server's DeadLetterReplayGateTests. The panel read `{ messages, nextId }`, so it showed none.
 */
interface StoreResult {
	totalCount?: number;
	pageNumber?: number;
	envelopes?: DeadLetterEnvelope[];
}

interface DeadLetterEnvelope {
	id: string;
	executionTime?: string | null;
	message?: Record<string, unknown> | null;
	messageType: string;
	receivedAt: string;
	source: string;
	exceptionType: string;
	exceptionMessage: string;
	sentAt: string;
	replayable: boolean;
}

export function parseDeadLetters(
	raw: unknown,
	page: number,
	limit: number,
): DeadLetterPage {
	// One result per store; a single result (an older host) is the same thing with one store.
	const stores = (Array.isArray(raw) ? raw : raw ? [raw] : []) as StoreResult[];

	const byId = new Map<string, Message>();
	for (const store of stores) {
		for (const envelope of store.envelopes ?? []) {
			if (byId.has(envelope.id)) continue;

			byId.set(envelope.id, {
				id: envelope.id,
				executionTime: envelope.executionTime ?? "",
				body: envelope.message ?? {},
				messageType: envelope.messageType,
				receivedAt: envelope.receivedAt,
				source: envelope.source,
				exceptionType: envelope.exceptionType,
				exceptionMessage: envelope.exceptionMessage,
				sentAt: new Date(envelope.sentAt),
				replayable: envelope.replayable,
			});
		}
	}

	// More to fetch while any store holds more than the pages asked for so far.
	const more = stores.some((store) => (store.totalCount ?? 0) > page * limit);

	return { messages: [...byId.values()], nextPage: more ? page + 1 : null };
}
